import React, { useState, useRef, useEffect } from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/solid';
import NavigationButton from "../../classResults/components/lessonNavigation/NavigationButton";

const MonthSelector = ({ selectedMonth, onMonthChange }) => {
  const [showCalendar, setShowCalendar] = useState(false);
  const [calendarDate, setCalendarDate] = useState(new Date());
  const calendarRef = useRef(null);
  
  // 제한 날짜 정의 (2025년 1월)
  const limitYear = 2025;
  const limitMonth = 1;
  
  // 현재 날짜 정보
  const currentDate = new Date();
  const currentYear = currentDate.getFullYear();
  const currentMonthNum = currentDate.getMonth() + 1;

  // 선택된 월이 변경될 때 달력 날짜도 업데이트
  useEffect(() => {
    if (selectedMonth.year && selectedMonth.month) {
      setCalendarDate(new Date(selectedMonth.year, selectedMonth.month - 1, 1));
    }
  }, [selectedMonth]);
  
  // 월이 제한 월보다 이전인지 확인
  const isBeforeLimitMonth = (year, month) => {
    return year < limitYear || (year === limitYear && month < limitMonth);
  };

  // 이전 월 이동
  const goToPreviousMonth = () => {
    let newYear = selectedMonth.year;
    let newMonth = selectedMonth.month - 1;
    
    if (newMonth < 1) {
      newMonth = 12;
      newYear = newYear - 1;
    }
    
    // 제한 월보다 이전으로는 이동할 수 없음
    if (isBeforeLimitMonth(newYear, newMonth)) {
      return;
    }
    
    onMonthChange({
      year: newYear,
      month: newMonth
    });
  };
  
  // 다음 월 이동 (현재 월까지만 허용)
  const goToNextMonth = () => {
    // 현재 월인지 확인
    if (isCurrentMonth()) {
      return; // 현재 월이라면 다음 월로 이동하지 않음
    }
    
    let newYear = selectedMonth.year;
    let newMonth = selectedMonth.month + 1;
    
    if (newMonth > 12) {
      newMonth = 1;
      newYear = newYear + 1;
    }
    
    // 현재 월보다 미래로는 이동할 수 없음
    if (newYear > currentYear || (newYear === currentYear && newMonth > currentMonthNum)) {
      return;
    }
    
    onMonthChange({
      year: newYear,
      month: newMonth
    });
  };
  
  // 이번 달로 이동
  const goToCurrentMonth = () => {
    onMonthChange({
      year: currentYear,
      month: currentMonthNum
    });
  };
  
  // 현재 월인지 확인
  const isCurrentMonth = () => {
    return selectedMonth.year === currentYear && selectedMonth.month === currentMonthNum;
  };
  
  // 이전 버튼 비활성화 여부 확인
  const isPrevButtonDisabled = () => {
    let prevYear = selectedMonth.year;
    let prevMonth = selectedMonth.month - 1;
    
    if (prevMonth < 1) {
      prevMonth = 12;
      prevYear = prevYear - 1;
    }
    
    return isBeforeLimitMonth(prevYear, prevMonth);
  };
  
  // 다음 버튼 비활성화 여부 확인
  const isNextButtonDisabled = () => {
    return isCurrentMonth();
  };
  
  // 달력 표시/숨김 토글
  const toggleCalendar = () => {
    setShowCalendar(!showCalendar);
  };
  
  // 달력에서 년도 변경
  const changeYear = (increment) => {
    const newDate = new Date(calendarDate);
    newDate.setFullYear(newDate.getFullYear() + increment);
    
    // 현재 년도보다 미래로는 이동할 수 없음
    if (increment > 0 && newDate.getFullYear() > currentYear) {
      return;
    }
    
    // 제한 년도보다 이전으로는 이동할 수 없음
    if (increment < 0 && newDate.getFullYear() < limitYear) {
      return;
    }
    
    setCalendarDate(newDate);
  };
  
  // 달력에서 월 선택
  const selectMonth = (monthIndex) => {
    const year = calendarDate.getFullYear();
    const month = monthIndex + 1;
    
    // 현재 월보다 미래는 선택할 수 없음
    if (year > currentYear || (year === currentYear && month > currentMonthNum)) {
      return;
    }
    
    // 제한 월보다 이전은 선택할 수 없음
    if (isBeforeLimitMonth(year, month)) {
      return;
    }
    
    onMonthChange({
      year: year,
      month: month
    });
    
    setShowCalendar(false);
  };
  
  // 월 이름 배열
  const monthNames = [
    '1월', '2월', '3월', '4월', '5월', '6월',
    '7월', '8월', '9월', '10월', '11월', '12월'
  ];
  
  // 달력 외부 클릭 감지
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (calendarRef.current && !calendarRef.current.contains(event.target)) {
        setShowCalendar(false);
      }
    };
    
    if (showCalendar) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showCalendar]);
  
  // 달력 렌더링
  const renderCalendar = () => {
    if (!showCalendar) return null;
    
    const year = calendarDate.getFullYear();
    
    return (
      <div className="bg-white rounded-lg shadow-lg p-4" ref={calendarRef}>
        {/* 년도 선택 헤더 */}
        <div className="flex justify-between items-center mb-4">
          <button 
            onClick={() => changeYear(-1)}
            disabled={year <= limitYear}
            className={`p-1 rounded-full ${
              year <= limitYear 
                ? 'text-gray-300 cursor-not-allowed' 
                : 'hover:bg-gray-200'
            }`}
          >
            <ChevronLeftIcon className="w-5 h-5" />
          </button>
          <div className="font-semibold">
            {year}년
          </div>
          <button 
            onClick={() => changeYear(1)}
            disabled={year >= currentYear}
            className={`p-1 rounded-full ${
              year >= currentYear 
                ? 'text-gray-300 cursor-not-allowed' 
                : 'hover:bg-gray-200'
            }`}
          >
            <ChevronRightIcon className="w-5 h-5" />
          </button>
        </div>
        
        {/* 월 선택 그리드 */}
        <div className="grid grid-cols-3 gap-2">
          {monthNames.map((monthName, index) => {
            const month = index + 1;
            const isSelected = selectedMonth.year === year && selectedMonth.month === month;
            const isFuture = year > currentYear || (year === currentYear && month > currentMonthNum);
            const isBeforeLimit = isBeforeLimitMonth(year, month);
            const isDisabled = isFuture || isBeforeLimit;
            
            return (
              <button
                key={index}
                onClick={() => !isDisabled && selectMonth(index)}
                disabled={isDisabled}
                className={`
                  p-2 text-center rounded cursor-pointer transition-colors
                  ${isSelected
                    ? 'bg-blue-200 font-bold'
                    : isDisabled
                      ? 'text-gray-300 cursor-not-allowed'
                      : 'hover:bg-blue-100 text-gray-700'
                  }
                  ${isBeforeLimit ? 'line-through' : ''}
                `}
              >
                {monthName}
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div className="mb-4 relative mt-[65px]">
      <div className="flex items-center justify-center space-x-4">
        <NavigationButton
          onClick={goToPreviousMonth}
          disabled={isPrevButtonDisabled()}
          className={`flex items-center justify-center w-10 h-10 rounded-full 
          ${isPrevButtonDisabled()
            ? 'bg-gray-100 cursor-not-allowed'
            : 'bg-blue-100 hover:bg-blue-200'
          }`}
          direction="prev"
        />

        <button
          onClick={toggleCalendar}
          className="flex items-center justify-center px-4 py-2 rounded-lg bg-white border border-gray-300 hover:bg-gray-50 min-w-[110px]"
        >
          <span className="text-lg font-medium">
            {selectedMonth.year}. {selectedMonth.month}
          </span>
        </button>

        <NavigationButton
          onClick={goToNextMonth}
          className={`flex items-center justify-center w-10 h-10 rounded-full 
          ${isNextButtonDisabled()
            ? 'bg-gray-100 cursor-not-allowed'
            : 'bg-blue-100 hover:bg-blue-200'
          }`}
          disabled={isNextButtonDisabled()}
          direction="next"
        />
      </div>

      {/* 이번달 버튼 */}
      <div className="flex justify-center mt-2">
        <button
          onClick={goToCurrentMonth}
          disabled={isCurrentMonth()}
          className={`text-sm px-3 py-1 rounded-md ${
            isCurrentMonth() 
              ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
              : 'bg-[#D7E5FE] text-[#5A85FF] hover:bg-blue-100 font-[700]'
          }`}
        >
          이번달
        </button>
      </div>

      {showCalendar && (
        <div className="absolute top-20 left-1/2 transform -translate-x-1/2 z-10 min-w-[300px]">
          {renderCalendar()}
        </div>
      )}
    </div>
  );
};

export default MonthSelector; 